// Phase-25.B — Multi-select category picker bound to the canonical registry.
//
// Used in: Rule Builder (replaces the free-text ChipList for Categories).
// Reads:  GET /api/categories (cached via useCategories hook)
// Writes: POST /api/admin/categories (only when admin clicks "Create new" in
//         the dropdown footer; the new category is pushed to the cache
//         optimistically so the picker shows it immediately).
//
// Behaviour
// =========
// • Selected categories render as removable chips above the trigger.
// • Trigger opens a Popover with a Command (search + checkboxes).
// • Search filters by value, label, AND aliases (so typing "groceries" still
//   surfaces the canonical "Grocery").
// • Footer offers a "+ Create new" option when the search term doesn't
//   exactly match any existing canonical value or alias.
// • Locked rules show chips as read-only (no remove, trigger disabled).
//
// Why a separate component (not just a Combobox in the editor):
//   The picker has special UX rules — alias-aware search, create-on-the-fly,
//   wallet-aware filtering in the future. Encapsulating keeps callers tidy.
import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Plus, X, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from '@/components/ui/command';
import { useCategories, invalidateCategories, pushCategoryOptimistic } from '@/hooks/useCategories';
import { AdminAPI } from '@/lib/admin-api';
import { toast } from 'sonner';

/**
 * Lowercase a string for case-insensitive matching. Treats null/undefined
 * as empty string so it never throws.
 */
const _norm = (s) => String(s || '').trim().toLowerCase();

/**
 * Resolve an inbound value to its canonical form (via aliases). Returns the
 * canonical value or the original if no match.
 */
function _resolveCanonical(value, categories) {
  const lower = _norm(value);
  if (!lower) return value;
  for (const c of categories) {
    if (_norm(c.value) === lower) return c.value;
    for (const a of (c.aliases || [])) {
      if (_norm(a) === lower) return c.value;
    }
  }
  return value;
}

export function CategoryMultiSelect({
  value = [],
  onChange,
  locked = false,
  placeholder = 'Select categories…',
  testid = 'category-multi-select',
  allowCreate = true,
}) {
  const { categories, loading, error } = useCategories();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  // Build a lookup of canonical values + aliases for fast filtering. Each
  // entry includes a 'haystack' string that the user's search is matched
  // against (case-insensitive substring).
  const entries = useMemo(() => {
    const list = (categories || []).filter((c) => c.is_active !== false);
    return list.map((c) => ({
      value: c.value,
      label: c.label || c.value,
      aliases: c.aliases || [],
      haystack: _norm(`${c.value} ${c.label || ''} ${(c.aliases || []).join(' ')}`),
    }));
  }, [categories]);

  // Current selection — normalise inbound values that might be aliases.
  // This means a rule that historically stored 'Groceries' (alias) will
  // show as canonical 'Grocery' in the picker, even before any save.
  const selectedSet = useMemo(() => {
    const out = new Set();
    for (const v of (value || [])) {
      const canonical = _resolveCanonical(v, categories || []);
      if (canonical) out.add(canonical);
    }
    return out;
  }, [value, categories]);

  // Filter entries by search.
  const filtered = useMemo(() => {
    const q = _norm(search);
    if (!q) return entries;
    return entries.filter((e) => e.haystack.includes(q));
  }, [entries, search]);

  // Should "+ Create new" affordance appear?
  const trimmed = search.trim();
  const exactMatch = trimmed && entries.some(
    (e) => _norm(e.value) === _norm(trimmed) || e.aliases.some((a) => _norm(a) === _norm(trimmed)),
  );
  const showCreate = allowCreate && trimmed.length > 0 && !exactMatch && !creating;

  const handleToggle = (val) => {
    if (locked) return;
    const next = new Set(selectedSet);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onChange(Array.from(next));
  };

  const handleCreate = async () => {
    const newValue = trimmed;
    if (!newValue) return;
    setCreating(true);
    try {
      const created = await AdminAPI.categories.create({ value: newValue });
      // Optimistic cache insert so picker immediately shows it
      pushCategoryOptimistic(created);
      invalidateCategories(); // server reconciliation
      onChange([...(value || []), created.value]);
      toast.success(`Category '${created.value}' created and added to the rule`);
      setSearch('');
    } catch (err) {
      // Surface the 409 dupe case helpfully (server runs normalize_category
      // first, so the canonical name might already exist even if the typed
      // string doesn't match exactly).
      if (err?.status === 409) {
        toast.error('That category already exists in the registry');
      } else {
        toast.error(err?.message || 'Could not create category');
      }
    } finally {
      setCreating(false);
    }
  };

  const selectedArr = Array.from(selectedSet);
  const triggerLabel = selectedArr.length === 0
    ? placeholder
    : `${selectedArr.length} ${selectedArr.length === 1 ? 'category' : 'categories'} selected`;

  return (
    <div className="space-y-2" data-testid={testid}>
      {selectedArr.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-chips`}>
          {selectedArr.map((v) => (
            <Badge
              key={v}
              variant="secondary"
              className="text-xs gap-1 pr-1"
              data-testid={`${testid}-chip-${v}`}
            >
              {v}
              {!locked && (
                <button
                  type="button"
                  className="ml-0.5 rounded-sm opacity-60 hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-ring"
                  onClick={() => handleToggle(v)}
                  aria-label={`Remove ${v}`}
                  data-testid={`${testid}-remove-${v}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={locked}
            className="w-full justify-between h-9 font-normal text-sm"
            data-testid={`${testid}-trigger`}
          >
            <span className={selectedArr.length === 0 ? 'text-muted-foreground' : ''}>
              {loading ? 'Loading categories…' : triggerLabel}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search by name or alias…"
              value={search}
              onValueChange={setSearch}
              data-testid={`${testid}-search`}
            />
            <CommandList
              // Phase-25 fix — `overscroll-contain` keeps the mouse wheel
              // event inside this list instead of bubbling to Radix's
              // Popover focus-trap (which is what blocked scrolling
              // before; keyboard arrows worked because Radix routes those
              // through cmdk's own keyboard handler, but wheel events
              // hit the trap first). `onWheelCapture` belt-and-suspenders
              // for older Radix versions.
              className="overscroll-contain"
              onWheelCapture={(e) => { e.stopPropagation(); }}
            >
              {error && (
                <div className="px-3 py-2 text-xs text-destructive">Failed to load categories.</div>
              )}
              <CommandEmpty>
                {trimmed ? `No category matches "${trimmed}"` : 'No categories yet.'}
              </CommandEmpty>
              <CommandGroup heading={`Canonical (${filtered.length})`}>
                {filtered.map((e) => {
                  const isSelected = selectedSet.has(e.value);
                  return (
                    <CommandItem
                      key={e.value}
                      value={e.value}
                      onSelect={() => handleToggle(e.value)}
                      data-testid={`${testid}-option-${e.value}`}
                      className="cursor-pointer"
                    >
                      <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-border'}`}>
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{e.value}</div>
                        {e.aliases.length > 0 && (
                          <div className="text-[10px] text-muted-foreground truncate">
                            aliases: {e.aliases.slice(0, 4).join(', ')}{e.aliases.length > 4 ? '…' : ''}
                          </div>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {showCreate && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={handleCreate}
                      className="cursor-pointer text-primary"
                      value={`__create__${trimmed}`}
                      data-testid={`${testid}-create-${trimmed}`}
                    >
                      {creating
                        ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        : <Plus className="mr-2 h-4 w-4" />}
                      <span className="text-sm">Create new category &ldquo;{trimmed}&rdquo;</span>
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default CategoryMultiSelect;
